import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, X, Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { adminApi } from "@/lib/adminApi";

const emptyForm = {
  date_label: "",
  date_iso: "",
  lieu: "",
  duree: "",
  places_restantes: 10,
  statut: "a_venir",
};

const statusOptions = [
  { value: "a_venir", label: "À venir" },
  { value: "ouvert", label: "Inscriptions ouvertes" },
  { value: "complet", label: "Complet" },
  { value: "archive", label: "Archivé" },
];

export default function WorkshopsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await adminApi.listWorkshops());
    } catch {
      toast.error("Impossible de charger les ateliers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const openEdit = (w) => {
    setEditing(w);
    setForm({
      date_label: w.date_label || "",
      date_iso: w.date_iso || "",
      lieu: w.lieu || "",
      duree: w.duree || "",
      places_restantes: w.places_restantes ?? 0,
      statut: w.statut || "a_venir",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date_label.trim() || !form.lieu.trim() || !form.duree.trim()) {
      toast.error("Merci de renseigner les champs obligatoires.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        places_restantes: Number(form.places_restantes),
        date_iso: form.date_iso || null,
      };
      if (editing) {
        await adminApi.updateWorkshop(editing.id, payload);
        toast.success("Atelier mis à jour.");
      } else {
        await adminApi.createWorkshop(payload);
        toast.success("Atelier créé.");
      }
      setOpen(false);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Supprimer l'atelier "${w.date_label}" ?`)) return;
    try {
      await adminApi.deleteWorkshop(w.id);
      toast.success("Atelier supprimé.");
      load();
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  return (
    <div data-testid="admin-workshops-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif-display text-2xl text-[#1E1E2A]">Ateliers</h2>
          <p className="text-sm text-[#5F6170] mt-1">
            Gérer les dates, lieux et places disponibles.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="admin-workshop-create"
          className="inline-flex items-center gap-2 bg-[#2847B8] text-white hover:bg-[#3A2E6E] transition-colors rounded-full px-5 py-2.5 font-medium text-sm"
        >
          <Plus className="w-4 h-4" />
          Nouvel atelier
        </button>
      </div>

      <div className="bg-white rounded-3xl border border-[#DDD7E2] overflow-hidden">
        {loading ? (
          <div className="p-16 flex items-center justify-center text-[#5F6170]">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center text-[#5F6170]">
            <Calendar className="w-6 h-6 text-[#C98FA3] mx-auto mb-3" />
            Aucun atelier pour le moment.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F3EEF4] text-[#3A2E6E] text-xs uppercase tracking-[0.16em]">
                <tr>
                  <th className="text-left px-5 py-4 font-medium">Date</th>
                  <th className="text-left px-5 py-4 font-medium">Lieu</th>
                  <th className="text-left px-5 py-4 font-medium">Durée</th>
                  <th className="text-left px-5 py-4 font-medium">Places</th>
                  <th className="text-left px-5 py-4 font-medium">Statut</th>
                  <th className="text-right px-5 py-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#DDD7E2]">
                {items.map((w) => (
                  <tr key={w.id} data-testid={`admin-workshop-row-${w.id}`} className="hover:bg-[#FAF7F8]">
                    <td className="px-5 py-4">
                      <div className="font-serif-display text-base text-[#1E1E2A]">{w.date_label}</div>
                      {w.date_iso && (
                        <div className="text-xs text-[#5F6170] mt-1">{w.date_iso}</div>
                      )}
                    </td>
                    <td className="px-5 py-4 text-[#5F6170]">{w.lieu}</td>
                    <td className="px-5 py-4 text-[#5F6170]">{w.duree}</td>
                    <td className="px-5 py-4">
                      <span className="font-medium text-[#3A2E6E]">{w.places_restantes}</span>
                    </td>
                    <td className="px-5 py-4">
                      <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-[#3A2E6E]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C98FA3]" />
                        {w.statut}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(w)}
                          data-testid={`admin-workshop-edit-${w.id}`}
                          className="p-2 rounded-full hover:bg-[#F3EEF4] text-[#3A2E6E]"
                          title="Modifier"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => remove(w)}
                          data-testid={`admin-workshop-delete-${w.id}`}
                          className="p-2 rounded-full hover:bg-[#F3EEF4] text-[#C98FA3]"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="admin-workshop-dialog"
          className="sm:max-w-lg bg-[#FAF7F8] border-[#DDD7E2] rounded-3xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
        >
          <div className="p-7">
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl font-light text-[#1E1E2A]">
                {editing ? "Modifier l'atelier" : "Nouvel atelier"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="admin-workshop-form">
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                  Libellé date*
                </label>
                <Input
                  data-testid="admin-workshop-date-label"
                  value={form.date_label}
                  onChange={(e) => setForm({ ...form, date_label: e.target.value })}
                  placeholder="Ex: Weekend Constellharmonie · 3-4 octobre 2026"
                  className="mt-2 bg-white border-[#DDD7E2] rounded-xl h-11"
                  disabled={saving}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                    Date ISO (compte à rebours)
                  </label>
                  <Input
                    data-testid="admin-workshop-date-iso"
                    type="date"
                    value={form.date_iso}
                    onChange={(e) => setForm({ ...form, date_iso: e.target.value })}
                    className="mt-2 bg-white border-[#DDD7E2] rounded-xl h-11"
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                    Places restantes*
                  </label>
                  <Input
                    data-testid="admin-workshop-places"
                    type="number"
                    min={0}
                    max={999}
                    value={form.places_restantes}
                    onChange={(e) =>
                      setForm({ ...form, places_restantes: e.target.value })
                    }
                    className="mt-2 bg-white border-[#DDD7E2] rounded-xl h-11"
                    disabled={saving}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                  Lieu*
                </label>
                <Input
                  data-testid="admin-workshop-lieu"
                  value={form.lieu}
                  onChange={(e) => setForm({ ...form, lieu: e.target.value })}
                  placeholder="Valromey sur Séran (01)"
                  className="mt-2 bg-white border-[#DDD7E2] rounded-xl h-11"
                  disabled={saving}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                  Durée*
                </label>
                <Input
                  data-testid="admin-workshop-duree"
                  value={form.duree}
                  onChange={(e) => setForm({ ...form, duree: e.target.value })}
                  placeholder="2 jours · samedi & dimanche"
                  className="mt-2 bg-white border-[#DDD7E2] rounded-xl h-11"
                  disabled={saving}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[#5F6170] font-medium">
                  Statut
                </label>
                <Select
                  value={form.statut}
                  onValueChange={(v) => setForm({ ...form, statut: v })}
                  disabled={saving}
                >
                  <SelectTrigger
                    data-testid="admin-workshop-statut"
                    className="mt-2 bg-white border-[#DDD7E2] rounded-xl h-11"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={saving}
                  className="flex-1 bg-white border border-[#DDD7E2] hover:border-[#C98FA3] text-[#1E1E2A] rounded-full h-11 font-medium inline-flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" /> Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="admin-workshop-submit"
                  className="flex-1 bg-[#2847B8] text-white hover:bg-[#3A2E6E] disabled:opacity-60 rounded-full h-11 font-medium inline-flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Enregistrement…
                    </>
                  ) : (
                    "Enregistrer"
                  )}
                </button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
